/**
 * 앱의 네이티브 스캐너를 부르고 결과를 기다린다.
 *
 * ── 왜 네이티브인가 ──
 *   카메라를 다루는 일(렌즈 고르기·초점·해상도)은 네이티브가 근본적으로 낫다.
 *   웹은 그것을 우회하느라 장치 **이름 문자열**을 정규식으로 파싱하고 있다
 *   (roiScanner 의 findMainBackLens). 안드로이드에서 초광각이 잡히면 최소 초점 거리가
 *   길어 QR 을 가까이 대면 흐린데, 렌즈 선택·연속 초점·매크로 전환은 네이티브의 기본
 *   기능이라 그 문제가 애초에 없다.
 *
 * ── 경계는 얇다 ──
 *   네이티브가 하는 일은 「카메라를 열고 박스 안만 읽어 값을 돌려주기」까지다.
 *   화면·연출·결과 판정은 웹에 남는다. ④ 제품코드의 OCR 다듬기도 웹에 남는다 —
 *   실측으로 맞춘 값이라(ocrCapture.js) 플랫폼을 옮기면 다시 잡아야 하고,
 *   옮겨 봐야 인식률이 오르지 않는다.
 *
 * ── 앱이 없으면 ──
 *   부르는 쪽에서 deviceCheck 의 hasNativeScanner() 로 먼저 거른다.
 *   여기까지 왔는데 메서드가 없다면 { unsupported: true } 로 돌려주므로,
 *   화면은 웹 카메라로 떨어지면 된다.
 *
 * ── bridges.js 의 webToApp 을 쓰지 않는 이유 ──
 *   겹쳐 보이지만 방식이 다르다. bridges.js 는 ⓐ UA 문자열로 플랫폼을 가르고
 *   ⓑ 메서드 이름을 코드에 박아 둔다. 그래서 스캔에는 못 쓴다 —
 *   ⓐ 는 「앱인가」만 알 뿐 「그 메서드가 있는가」를 모르므로 구버전 앱에서 화면이 멈추고,
 *   ⓑ 는 메서드가 늘 때마다 같은 분기를 한 벌씩 더 쓰게 만든다.
 *
 *   여기서는 메서드 이름을 인자로 받고 그 존재로 플랫폼을 가른다.
 *   기존 로그인 브릿지를 이 방식으로 옮기는 것은 별개 일이라 손대지 않았다 —
 *   잘 돌고 있는 코드고, 이 작업의 범위가 아니다.
 */

import { hasNativeScanner, isIOS } from './deviceCheck';

/*
  기다리는 시간에 한계를 두지 않는다.

  처음에는 「앱이 죽었을 때를 걸러내는 그물」로 2분 제한을 뒀는데, 실기기에서
  그것이 그대로 버그가 됐다 — 사용자가 라벨을 찾느라 2분을 넘기자 웹이 제멋대로
  네이티브를 포기하고 웹 카메라로 떨어졌다. 그 순간 화면 투명 처리도 걷혀
  「카메라는 도는데 화면이 하얗다」가 됐다.

  「오래 들고 있는 것」과 「앱이 죽은 것」은 구분할 수 없다. 그리고 구분할 필요도 없다 —
  앱은 어떤 경로로 끝나든 반드시 답하기로 되어 있고(취소·권한거부 포함),
  화면을 떠나면 abortNativeScan 이 약속을 정리한다. 그물이 없어도 새는 곳이 없다.
*/

/**
 * 기다리고 있는 요청. 한 번에 하나뿐이다 — 스캐너는 전체 화면이라 둘이 겹칠 수 없다.
 * { resolve }
 */
let pending = null;

function settle(result) {
    if (!pending) return;
    const { resolve } = pending;
    pending = null;
    resolve(result);
}

/**
 * 앱이 결과를 돌려줄 때 부른다. App.vue 가 window 에 붙인다.
 *
 * 안드로이드는 evaluateJavascript 로 문자열을 넘기고 iOS 는 객체를 넘길 수 있어
 * 둘 다 받는다.
 *
 * @param {string|object} input { raw } · { barcodes } · { image, w, h } · { cancelled: true } · { denied: true }
 */
export function receiveNativeScanResult(input) {
    let data = input;
    if (typeof input === 'string') {
        try {
            data = JSON.parse(input);
        } catch {
            // 문자열 하나만 던져 주는 앱 버전을 위해 남겨 둔다
            data = { raw: input };
        }
    }
    settle(data || {});
}

/**
 * 네이티브 스캐너를 열고 결과를 기다린다.
 *
 * 던지지 않는다 — 실패도 결과의 한 종류로 돌려준다. 부르는 쪽이 화면을 어떻게 할지
 * 정해야 하는데, 예외로 만들면 그 분기가 try/catch 안으로 숨는다.
 *
 * @param  {string} method  'requestWebToApp_ScanQR_Label' 처럼
 * @param  {object} payload 앱에 함께 넘길 값 (지금은 비어 있다)
 * @return {Promise<object>} { raw } · { barcodes } · { image, w, h }
 *                           · { cancelled: true } 앱이 스캔을 끝냈다
 *                           · { cancelled: true, byWeb: true } 웹이 스스로 끝냈다
 *                           · { denied: true } 카메라 권한이 없어 앱이 카메라를 못 켰다
 *                           · { unsupported: true } 그 메서드가 없는 앱이다
 */
export function callNativeScanner(method, payload = {}) {
    return new Promise((resolve) => {
        /*
          앞의 요청이 남아 있으면 취소로 정리한다.

          화면을 빠르게 오가면 이전 약속이 영영 안 풀린 채 남는다. 그 상태로 새 요청을
          받으면 앞의 것을 덮어써 버려, 기다리던 쪽은 아무 답도 못 받는다.

          byWeb 을 붙이는 이유: 앱이 보낸 취소와 구분해야 한다. 셔터·예시 여닫기처럼 웹이
          새로 청하면서 앞 요청을 접는 것은 정상 흐름이다. 반면 앱이 스스로 취소를 보냈다면
          카메라를 못 켰다는 뜻이다(권한 거부를 따로 알리기 전의 앱은 거부도 취소로 보낸다).
        */
        if (pending) settle({ cancelled: true, byWeb: true });

        pending = { resolve };

        try {
            if (typeof window.WebViewBridge?.[method] === 'function') {
                // 안드로이드 — @JavascriptInterface 는 문자열만 받는다
                window.WebViewBridge[method](JSON.stringify(payload));
            } else if (window.webkit?.messageHandlers?.[method]) {
                // iOS — postMessage 는 객체를 그대로 받는다
                window.webkit.messageHandlers[method].postMessage(payload);
            } else {
                settle({ unsupported: true });
            }
        } catch (error) {
            console.warn('[nativeScanner] 요청 실패:', error?.message);
            settle({ unsupported: true });
        }
    });
}

/**
 * 화면에 그려진 사각형을 앱에 알려 줄 모양으로 잰다.
 *
 * **비율(0~1)로 준다.** 웹은 CSS 픽셀로 재고 네이티브 뷰는 기기 픽셀이라
 * 그대로 넘기면 화면 밀도만큼 어긋난다. 비율이면 밀도·확대·뷰포트 설정과 무관하다.
 *
 * 기준은 카메라가 깔린 자리다. 앱은 웹뷰와 같은 자리에 미리보기를 채우므로
 * 화면 전체(innerWidth·innerHeight)를 기준으로 삼으면 맞아떨어진다 —
 * 우리 스캔 화면의 카메라가 화면 전체를 채우기 때문이다(.mk-cam-full).
 *
 * @param  {HTMLElement} boxEl 조준 틀 (.mk-scan-box · .mk-shot-box)
 * @return {{roi: {left,top,right,bottom}}|{}} 못 재면 빈 객체 — 앱이 전체를 읽는다
 */
export function roiPayloadOf(boxEl) {
    if (!boxEl) return {};
    const b = boxEl.getBoundingClientRect();
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (!b.width || !b.height || !w || !h) return {};
    return {
        roi: {
            left: b.left / w,
            top: b.top / h,
            right: b.right / w,
            bottom: b.bottom / h,
        },
    };
}

/**
 * 앱 카메라가 웹 **뒤에** 있을 때, 페이지 뿌리까지 투명하게 만든다.
 *
 * ── 왜 필요한가 ──
 *   영상은 앱이 웹뷰 **뒤에** 깐다. 페이지가 불투명하면 그 영상을 덮어 까맣거나
 *   허옇게만 보인다. 실기기에서 정확히 그랬다 — 카메라는 열리고 QR 도 읽혔는데
 *   화면만 안 보였다(CameraDevice.onOpened 까지 로그로 확인).
 *
 * ── 왜 CSS 가 아니라 자바스크립트인가 ──
 *   mobile-kit.scss 는 각 화면의 <style scoped> 안에서 @import 된다. 그러면 Vue 가
 *   선택자 끝에 [data-v-…] 를 붙인다.
 *
 *       html.mk-native-camera body   →   html.mk-native-camera body[data-v-…]
 *
 *   body·#app 에는 그 속성이 없으니 **영원히 안 맞는다.** 실제로 그렇게 만들었다가
 *   화면이 안 걷혔다. 여기서 직접 지정해야 확실하다.
 *
 * ── 무엇을 걷나 ──
 *   스테이지 하나만 걷어서는 소용이 없다. 그 위로 셸·판·#app·body 가 각자 색을 칠하고
 *   있어 네 겹이 영상을 덮는다.
 *
 * 스캔하는 동안만 걷고 끝나면 되돌린다 — 계속 걷어 두면 다른 화면의 흰 바탕이 사라져
 * 글자가 안 읽힌다.
 */
/*
  앱 카메라가 웹 뒤에 있을 때, 스캔 화면부터 뿌리까지 길을 뚫는다.

  ── 왜 목록이 아니라 조상을 훑나 ──
    처음에는 걷을 요소를 이름으로 나열했는데(html·body·#app·셸…) 화면마다 조상이 달라
    자꾸 샜다. 제품코드 촬영은 mobile-panel 안에 열려서 .mk-panel 이 카메라를 덮고
    있었다. 화면이 늘 때마다 목록을 고쳐야 하는 구조라 실제 조상을 훑는 것으로 바꿨다.

  ── 왜 형제를 감추나 ──
    판(.mk-panel)은 이전 화면 위에 덮이는 전체 화면이다. 판까지 투명하게 만들면
    **판 뒤에 있던 화면이 카메라 위로 비친다** — 실기기에서 「3/3 제품코드를 입력해
    주세요」가 카메라에 겹쳐 나왔다. 배경만 걷어서는 그 글자와 카드가 그대로 남는다.
    그래서 판을 지난 뒤부터는 지나온 길 밖의 형제를 감춘다.

    판이 없는 화면(라벨 QR)은 감출 것이 없다 — 뒤에 가릴 화면 자체가 없기 때문이다.
*/

/** 되돌릴 때 쓰려고 원래 값을 적어 둔다 */
let savedBackgrounds = null;
let hiddenSiblings = null;

export function setNativeCameraBackdrop(on) {
    if (typeof document === 'undefined') return;

    if (!on) {
        (savedBackgrounds || []).forEach(([el, before]) => {
            el.style.removeProperty('background');
            if (before) el.style.background = before;
        });
        (hiddenSiblings || []).forEach(([el, before]) => {
            el.style.removeProperty('visibility');
            if (before) el.style.visibility = before;
        });
        savedBackgrounds = null;
        hiddenSiblings = null;
        return;
    }

    // 이미 걷어 뒀더라도 다시 훑는다 — 그 사이 판이 열려 조상이 늘었을 수 있다
    if (savedBackgrounds) setNativeCameraBackdrop(false);

    const stage = document.querySelector('.mk-scan-stage');
    if (!stage) return;

    savedBackgrounds = [];
    hiddenSiblings = [];

    let child = stage;
    let el = stage.parentElement;
    let pastPanel = false;

    while (el) {
        savedBackgrounds.push([el, el.style.background]);
        // #app 의 배경이 !important 라 이쪽도 important 로 이겨야 한다
        el.style.setProperty('background', 'transparent', 'important');

        if (pastPanel) {
            Array.from(el.children).forEach((sib) => {
                if (sib === child) return;
                /*
                  상단 로고 바와 하단 탭은 남긴다.

                  판을 지난 뒤 형제를 감추는 것은 **판 뒤에 깔린 화면**이 카메라 위로
                  비치는 것을 막으려는 것이다. 이 둘은 그 화면이 아니라 항상 떠 있는
                  틀이고, 제 배경을 갖고 있어 영상이 비쳐 보이지도 않는다.

                  감추면 라벨 QR(판이 없어 아무것도 안 감춤)과 제품코드(판이 있어 감춤)의
                  화면이 서로 달라진다. 같은 카메라인데 한쪽만 사라진다.
                  하단 탭에서 실제로 그 증상이 났었다.

                  상단 로고 바(.mobile-home-bar)도 앱이 네이티브 바를 걷어내면서 웹이
                  그리게 된 뒤로 같은 처지가 됐다. 그 화면에서 로고를 아예 안 그릴지는
                  App.vue 의 showMobileHomeBar 가 정한다 — 여기서는 「그리기로 한 것을
                  스캔 중에만 몰래 감추지 않는다」까지만 책임진다.
                */
                if (sib.classList?.contains('mobile-tab-bar')) return;
                if (sib.classList?.contains('mobile-home-bar')) return;
                hiddenSiblings.push([sib, sib.style.visibility]);
                sib.style.visibility = 'hidden';
            });
        }
        if (el.classList?.contains('mk-panel')) pastPanel = true;

        child = el;
        el = el.parentElement;
    }
}

/*
  화면을 걷는 시점을 미룬다 (검은 화면 방지).

  ── 무엇이 문제였나 ──
    웹은 앱에 카메라를 청하자마자 페이지를 투명하게 만들었다. 그런데 앱이 미리보기를
    띄우기까지 시간이 걸린다. 그 사이 뒤에 아무것도 없어 **빈 화면**이 보인다
    (안드로이드는 검정, iOS 는 흰색). 실측 — 브릿지 호출부터 첫 프레임까지 약 0.5초.

    최초 1회는 훨씬 길다. 앱이 카메라 권한 팝업을 띄우기 때문이다.
    실측 3.3초 동안 통째로 빈 화면이었다.

  ── 왜 타이머인가 ──
    앱이 「미리보기 시작됨」을 알려 주지 않는다. 브릿지는 **읽은 값**이 있을 때만
    답하므로 준비 신호가 없다. 그래서 쓸 수 있는 신호 둘을 쓴다.

      ① 시간            보통 경로는 0.5초면 뜬다
      ② 페이지 가시성    권한 팝업이 뜨면 앱이 가려져 hidden 이 된다.
                        돌아온 뒤부터 다시 재면 팝업 시간을 통째로 건너뛴다

    앱이 준비 신호를 보내 주게 되면 이 함수는 그것으로 대체하면 된다.
*/
const REVEAL_DELAY = 500;

/**
 * @param  {function} onReveal 걷을 때 부를 것
 * @return {function} 취소 함수 — 화면을 떠날 때 반드시 부른다
 */
export function revealNativeCamera(onReveal) {
    let timer = null;
    let done = false;

    const fire = () => {
        if (done) return;
        done = true;
        cleanup();
        onReveal();
    };

    const restart = () => {
        if (done) return;
        if (timer) clearTimeout(timer);
        timer = setTimeout(fire, REVEAL_DELAY);
    };

    // 팝업이 닫혀 앱이 돌아왔다 — 그때부터 다시 잰다
    const onVisible = () => {
        if (document.visibilityState === 'visible') restart();
    };

    function cleanup() {
        if (timer) clearTimeout(timer);
        timer = null;
        document.removeEventListener('visibilitychange', onVisible);
    }

    document.addEventListener('visibilitychange', onVisible);
    restart();

    return () => {
        done = true;
        cleanup();
    };
}

/*
  앱 카메라의 확대 배율을 바꾼다.

  ── 왜 따로 필요한가 ──
    웹 카메라는 MediaStreamTrack 의 applyConstraints({zoom}) 로 배율을 바꾼다.
    앱이 카메라를 쥐고 있으면 웹에는 그 트랙이 없어 손댈 방법이 없다.
    그래서 앱에 「이 배율로 바꿔라」를 따로 알린다.

  ── 범위를 왜 앱에서 받아 오지 않나 ──
    브릿지는 스캔 결과를 돌려주는 통로 하나뿐이고, 값을 물어보는 길이 없다.
    범위를 받아 오려면 통로를 하나 더 만들어야 하는데 그만한 값이 아니다.
    그래서 웹은 1~3배로 고정해 보여 주고, **앱이 제 한계로 자른다.**
    3배를 못 내는 기기에서는 슬라이더 끝이 그 기기의 최대에 머문다.

  답을 기다리지 않는다 — 배율은 즉시 반영되는 값이라 확인할 것이 없다.
*/
const ZOOM_METHOD = 'requestWebToApp_SetZoom';

export function setNativeZoom(zoom) {
    const payload = { zoom };
    try {
        if (typeof window.WebViewBridge?.[ZOOM_METHOD] === 'function') {
            window.WebViewBridge[ZOOM_METHOD](JSON.stringify(payload));
        } else if (window.webkit?.messageHandlers?.[ZOOM_METHOD]) {
            window.webkit.messageHandlers[ZOOM_METHOD].postMessage(payload);
        }
    } catch (error) {
        console.warn('[nativeScanner] 배율 변경 실패:', error?.message);
    }
}

/**
 * 앱에 「그만 찍어라」를 알리는 메서드 이름.
 *
 * 앱은 웹뷰 **뒤에** 카메라를 깔고 웹뷰를 투명하게 만들어 둔다. 스캔 화면을 떠날 때
 * 앱에 알리지 않으면 그 상태가 그대로 남아, 카메라가 계속 돌고 **이후 화면이 전부
 * 투명한 배경 위에 그려진다.** 결과를 읽고 끝나는 길만으로는 부족하다 —
 * 사용자는 아무것도 안 찍고 뒤로 갈 수 있다.
 */
const STOP_METHOD = 'requestWebToApp_StopScan';

/**
 * 기다리던 요청을 버리고, 앱이 켜 둔 카메라도 끈다. 화면을 떠날 때 부른다.
 *
 * 약속만 버리면 웹 쪽은 깨끗해지지만 앱은 계속 찍고 있다.
 * 그래서 앱에도 함께 알린다 — 있으면 부르고, 없으면(구버전 앱·브라우저) 넘어간다.
 */
export function abortNativeScan() {
    settle({ cancelled: true, byWeb: true });
    setNativeCameraBackdrop(false);

    try {
        if (typeof window.WebViewBridge?.[STOP_METHOD] === 'function') {
            window.WebViewBridge[STOP_METHOD]('{}');
        } else if (window.webkit?.messageHandlers?.[STOP_METHOD]) {
            window.webkit.messageHandlers[STOP_METHOD].postMessage({});
        }
    } catch (error) {
        console.warn('[nativeScanner] 중단 요청 실패:', error?.message);
    }
}

/*
  앱의 설정 화면을 연다 — 꺼진 카메라 권한을 켜러 보내는 길.

  웹 페이지는 휴대폰 설정을 직접 열 수 없다. 설정 주소로 이동해도 앱이 웹뷰 안에서
  열려고 해 아무 일이 없다. 그래서 앱이 이 메서드로 열어 준다.

    iOS         설정 앱의 **이 앱 화면** — 카메라 스위치가 바로 보인다
    안드로이드   **앱 정보** 화면까지만 — 카메라 스위치로 곧장 보내는 공식 방법이 없다

  답을 기다리지 않는다. 설정 앱으로 넘어가면 돌려받을 것이 없다.
*/
const SETTINGS_METHOD = 'requestWebToApp_OpenAppSettings';

/**
 * 앱이 설정 화면을 열어 줄 수 있는가.
 *
 * 이 메서드가 없는 앱(이전 버전)에는 [설정으로 이동] 버튼을 그리지 않는다.
 * 눌러도 아무 일이 없는 버튼은 안내보다 나쁘다 — 업데이트 안내에 스토어 버튼을
 * 두지 않은 것과 같은 이유다(MobileScanner 의 ⓪).
 */
export const canOpenAppSettings = () => hasNativeScanner(SETTINGS_METHOD);

export function openAppSettings() {
    try {
        if (typeof window.WebViewBridge?.[SETTINGS_METHOD] === 'function') {
            window.WebViewBridge[SETTINGS_METHOD]('{}');
        } else if (window.webkit?.messageHandlers?.[SETTINGS_METHOD]) {
            window.webkit.messageHandlers[SETTINGS_METHOD].postMessage({});
        }
    } catch (error) {
        console.warn('[nativeScanner] 설정 열기 실패:', error?.message);
    }
}

/**
 * 앱 카메라를 못 켰을 때 보여 줄 말. { text, hint }
 *
 * ── 두 경우 ──
 *   denied   앱이 「권한이 없다」고 분명히 알려 왔다
 *   그 외     앱이 스스로 취소했다. 권한 거부를 따로 알리기 전의 앱은 거부도 취소로
 *            보내므로 거부라고 단정할 수 없다. 그래서 말을 넓게 잡는다
 *
 * ── 플랫폼마다 다른 이유 ──
 *   설정으로 보내면 iOS 는 카메라 스위치가 있는 화면에 바로 떨어지지만, 안드로이드는
 *   앱 정보 화면까지만 간다(openAppSettings 주석). 그래서 안드로이드만 두 단계를 더 적는다.
 *   메뉴 이름은 제조사마다 조금씩 다를 수 있어 실기기로 맞춰 볼 값이다.
 *
 * @param {object} result callNativeScanner 가 돌려준 값
 */
export function nativeCameraBlockedText(result) {
    if (!result?.denied) {
        return {
            text: '카메라를 켜지 못했어요',
            hint: '카메라 권한이 꺼져 있다면 휴대폰 설정에서 이 앱의 카메라를 허용해 주세요.',
        };
    }
    return {
        text: '카메라 권한이 꺼져 있어요',
        hint: isIOS()
            ? '설정에서 카메라를 켜 주세요.'
            : "설정 → 권한 → 카메라에서 '앱 사용 중에만 허용'을 선택해 주세요.",
    };
}
